import { ProtectedServerPlaceEvent } from 'gleblu-socket-api';
import moment from 'moment-timezone';
import { Action } from 'redux-act';
import { put, takeEvery } from 'redux-saga/effects';

import { fromDto as fromMergedTableDto, toDto as toMergedTableDto } from '../../../models/adapters/MergedTableAdapter';
import { fromDto as reservationFromDto, toDto as toReservationDto } from '../../../models/adapters/ReservationAdapter';
import { toDto as toReservationSeatingDto } from '../../../models/adapters/ReservationSeatingAdapter';
import { SeatedReservation } from '../../../models/interfaces/reservation/Reservation';
import { successMap } from '../../../models/interfaces/reservation/ReservationAcceptState';
import ReservationRejectReason, { failureMap } from '../../../models/interfaces/reservation/ReservationRejectReason';
import {
  reservationUpdated,
  reservationUpdateFailed,
  ReservationUpdateFailedPayload,
  updateReservation,
  UpdateReservationPayload,
} from '../../actions/reservation/updateReservation';
import getAuthenticationToken from '../getAuthenticationToken';
import getProtectedResponse from '../getProtectedResponse';

function* updateReservationSaga({ payload }: Action<UpdateReservationPayload>) {
  const { pendingReservation, seating, mergedTable } = payload;
  const reservationDto = toReservationDto(pendingReservation);
  const reservationSeatingDto = seating ? toReservationSeatingDto(seating) : null;
  const mergedTableDto = mergedTable ? toMergedTableDto(mergedTable) : null;
  const token = yield getAuthenticationToken();
  const socketPayload = { reservationDto, reservationSeatingDto, mergedTableDto };
  const response = yield getProtectedResponse(ProtectedServerPlaceEvent.updateReservation, token, socketPayload);
  const { error, reservationConfirmation } = response;
  const timestamp = moment();
  const unknownRejectPayload: ReservationUpdateFailedPayload = {
    timestamp,
    reservationUuid: pendingReservation.uuid,
    rejectReason: ReservationRejectReason.Unknown,
  };

  if (error || !reservationConfirmation) {
    yield put(reservationUpdateFailed(unknownRejectPayload));
  } else if (successMap.has(reservationConfirmation)) {
    const { acceptedReservation: acceptedReservationDto, createdMergedTable: createdMergedTableDto } = response;
    const acceptedReservation = reservationFromDto(acceptedReservationDto) as SeatedReservation;
    const createdMergedTable = createdMergedTableDto ? fromMergedTableDto(createdMergedTableDto) : null;
    yield put(
      reservationUpdated({
        reservation: acceptedReservation,
        reservationUuid: acceptedReservation.uuid,
        timestamp,
        createdMergedTable,
      })
    );
  } else if (failureMap.has(reservationConfirmation)) {
    const failedPayload = {
      timestamp,
      rejectReason: failureMap.get(reservationConfirmation),
      reservationUuid: pendingReservation.uuid,
    };

    yield put(reservationUpdateFailed(failedPayload));
  } else {
    yield put(reservationUpdateFailed(unknownRejectPayload));
  }
}

export default function* watchUpdateReservationSaga() {
  yield takeEvery(updateReservation, updateReservationSaga);
}
